import React, { useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Activity, Waves, Heart, Brain, Sparkles, Users, Target } from 'lucide-react';

interface DimensionScore {
  dimension: string;
  score: number; // 0-100
  icon: any;
  color: string;
  lastUpdated?: string;
  trend?: 'up' | 'down' | 'stable';
}

interface WellnessRadarProps {
  scores: DimensionScore[];
  size?: number;
  showLabels?: boolean;
  interactive?: boolean;
  onDimensionClick?: (dimension: string) => void;
}

export default function WellnessRadar({ 
  scores, 
  size = 400, 
  showLabels = true,
  interactive = false,
  onDimensionClick
}: WellnessRadarProps) {
  const center = size / 2;
  const radius = (size / 2) - 80;
  const angleStep = (2 * Math.PI) / scores.length;

  // Calculate polygon points for the actual scores
  const dataPoints = useMemo(() => {
    return scores.map((item, index) => {
      const angle = angleStep * index - Math.PI / 2; // Start from top
      const distance = (item.score / 100) * radius;
      const x = center + distance * Math.cos(angle);
      const y = center + distance * Math.sin(angle);
      return { x, y, ...item };
    });
  }, [scores, angleStep, radius, center]);

  // Calculate background circles (25%, 50%, 75%, 100%)
  const backgroundCircles = [25, 50, 75, 100].map(percent => ({
    radius: (percent / 100) * radius,
    opacity: 0.05 + (percent / 100) * 0.1
  }));

  // Create axis lines
  const axisLines = scores.map((_, index) => {
    const angle = angleStep * index - Math.PI / 2;
    const x = center + radius * Math.cos(angle);
    const y = center + radius * Math.sin(angle);
    return { x1: center, y1: center, x2: x, y2: y };
  });

  // Create the data polygon path
  const polygonPath = dataPoints.map((point, index) => 
    `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
  ).join(' ') + ' Z';

  // Overall wellness score (average)
  const overallScore = Math.round(scores.reduce((sum, s) => sum + s.score, 0) / scores.length);

  // Get status color - using brand colors
  const getStatusColor = (score: number) => {
    if (score >= 70) return 'text-primary';
    if (score >= 40) return 'text-accent';
    return 'text-destructive';
  };

  const getStatusBg = (score: number) => {
    if (score >= 70) return 'from-primary/20 to-primary/5 border-primary/30';
    if (score >= 40) return 'from-accent/20 to-accent/5 border-accent/30';
    return 'from-destructive/20 to-destructive/5 border-destructive/30';
  };

  // Calculate label positions around the perimeter
  const labelPositions = useMemo(() => {
    return scores.map((item, index) => {
      const angle = angleStep * index - Math.PI / 2;
      const labelDistance = radius + 40; // Position labels outside the radar
      const x = center + labelDistance * Math.cos(angle);
      const y = center + labelDistance * Math.sin(angle);
      return { x, y, ...item };
    });
  }, [scores, angleStep, radius, center]);

  return (
    <div className="space-y-6">
      {/* Educational explanation */}
      <Card className="p-6 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Brain className="w-5 h-5 text-primary" />
          How to Read Your Wellness Radar
        </h3>
        <div className="space-y-2 text-sm text-muted-foreground">
          <p>
            <strong className="text-foreground">The closer to the edge, the stronger you are</strong> in that dimension. 
            A balanced life shows an even shape, while spikes or dips reveal areas of strength or growth opportunity.
          </p>
          <p>
            <strong className="text-foreground">What the shape tells you:</strong> A balanced polygon means holistic wellness. 
            Uneven shapes show where to focus your energy for greater life satisfaction.
          </p>
        </div>
      </Card>

      <div className="flex flex-col lg:flex-row gap-8 items-center">
        {/* Radar Chart - Now larger and more prominent */}
        <div className="relative flex-1">
          <svg width={size} height={size} className="mx-auto filter drop-shadow-lg">
            {/* Background circles with improved visibility */}
            {backgroundCircles.map((circle, index) => (
              <circle
                key={index}
                cx={center}
                cy={center}
                r={circle.radius}
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                opacity={0.15 + (index * 0.1)}
                strokeDasharray={index === 3 ? "0" : "5,5"}
                className="text-muted-foreground animate-pulse"
                style={{ animationDelay: `${index * 200}ms`, animationDuration: '3s' }}
              />
            ))}

            {/* Axis lines */}
            {axisLines.map((line, index) => (
              <line
                key={index}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                stroke="currentColor"
                strokeWidth="1.5"
                opacity="0.25"
                className="text-muted-foreground"
              />
            ))}

            {/* Glow effect for data polygon */}
            <path
              d={polygonPath}
              fill="currentColor"
              fillOpacity="0.1"
              stroke="currentColor"
              strokeWidth="3"
              className="text-primary"
              style={{ filter: 'blur(8px)' }}
            />

            {/* Main data polygon with animation */}
            <path
              d={polygonPath}
              fill="currentColor"
              fillOpacity="0.3"
              stroke="currentColor"
              strokeWidth="3"
              className="text-primary transition-all duration-700 ease-in-out"
              style={{ 
                filter: 'drop-shadow(0 0 8px currentColor)',
                animation: 'pulse 2s ease-in-out infinite'
              }}
            />

            {/* Data points with enhanced visibility */}
            {dataPoints.map((point, index) => (
              <g key={index} className="animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                {/* Outer glow ring */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="14"
                  fill="currentColor"
                  fillOpacity="0.2"
                  className={point.color}
                />
                {/* Main point */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="10"
                  fill="currentColor"
                  className={`${point.color} cursor-pointer transition-all duration-300 hover:scale-125`}
                  onClick={() => interactive && onDimensionClick?.(point.dimension)}
                  style={{ 
                    filter: 'drop-shadow(0 3px 6px rgba(0,0,0,0.3))'
                  }}
                />
                {/* Inner highlight */}
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="4"
                  fill="white"
                  fillOpacity="0.7"
                />
              </g>
            ))}

            {/* Dimension labels around the perimeter */}
            {labelPositions.map((label, index) => {
              const Icon = label.icon;
              return (
                <g key={index} className="animate-fade-in" style={{ animationDelay: `${index * 100}ms` }}>
                  {/* Background circle for label */}
                  <circle
                    cx={label.x}
                    cy={label.y}
                    r="28"
                    fill="currentColor"
                    className="text-background"
                    opacity="0.95"
                  />
                  <circle
                    cx={label.x}
                    cy={label.y}
                    r="28"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className={label.color}
                    opacity="0.5"
                  />
                  {/* Icon */}
                  <foreignObject
                    x={label.x - 12}
                    y={label.y - 20}
                    width="24"
                    height="24"
                  >
                    <div className="flex items-center justify-center">
                      <Icon className={`w-5 h-5 ${label.color}`} />
                    </div>
                  </foreignObject>
                  {/* Label text */}
                  <text
                    x={label.x}
                    y={label.y + 8}
                    textAnchor="middle"
                    className="text-[10px] font-bold fill-current"
                    style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.3))' }}
                  >
                    {label.dimension}
                  </text>
                </g>
              );
            })}
          </svg>
        </div>

        {/* Overall Score - Now separate and balanced */}
        <div className="lg:w-80">
          <Card className={`p-8 bg-gradient-to-br ${getStatusBg(overallScore)} border-2 shadow-xl`}>
            <div className="text-center space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground tracking-wider uppercase">
                Overall Wellness Score
              </h3>
              <div className={`text-7xl font-bold ${getStatusColor(overallScore)} animate-fade-in`}>
                {overallScore}
              </div>
              <p className="text-sm text-muted-foreground">
                {overallScore >= 70 && "You're thriving! Your wellness is well-balanced across multiple dimensions."}
                {overallScore >= 40 && overallScore < 70 && "You're doing well, with room to grow. Focus on the dimensions that need attention."}
                {overallScore < 40 && "This is your starting point. Small improvements in any dimension will boost your overall wellness."}
              </p>
              <div className="pt-4 border-t border-border/50">
                <p className="text-xs text-muted-foreground">
                  Based on {scores.length} wellness dimensions
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Dimension details cards */}
      {showLabels && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {scores.map((item, index) => {
            const Icon = item.icon;
            return (
              <button
                key={index}
                onClick={() => interactive && onDimensionClick?.(item.dimension)}
                className={`p-4 rounded-xl border-2 bg-gradient-to-br from-background to-muted/30 transition-all hover-scale animate-fade-in ${
                  interactive ? 'hover:border-primary hover:shadow-lg cursor-pointer hover:-translate-y-1' : 'border-border'
                }`}
                style={{ animationDelay: `${index * 80}ms` }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`p-1.5 rounded-lg bg-gradient-to-br ${item.color.replace('text-', 'from-')}/20 ${item.color.replace('text-', 'to-')}/5`}>
                    <Icon className={`w-4 h-4 ${item.color}`} />
                  </div>
                  <span className="text-xs font-semibold">{item.dimension}</span>
                </div>
                <div className="flex items-end justify-between mt-3">
                  <span className={`text-2xl font-bold ${item.color}`}>
                    {item.score}
                  </span>
                  {item.trend && (
                    <span className={`text-sm font-bold ${
                      item.trend === 'up' ? 'text-green-500' : 
                      item.trend === 'down' ? 'text-red-500' : 
                      'text-muted-foreground'
                    }`}>
                      {item.trend === 'up' ? '↑' : item.trend === 'down' ? '↓' : '→'}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Helper function to calculate dimension scores from check-in data
export function calculateDimensionScores(data: any): DimensionScore[] {
  return [
    {
      dimension: 'Energy',
      score: (data.energy_level || 5) * 10,
      icon: Waves,
      color: 'text-accent',
      trend: data.energy_level > 7 ? 'up' : data.energy_level < 4 ? 'down' : 'stable'
    },
    {
      dimension: 'Body',
      score: calculateBodyScore(data),
      icon: Activity,
      color: 'text-accent',
      trend: 'stable'
    },
    {
      dimension: 'Emotion',
      score: calculateEmotionScore(data),
      icon: Heart,
      color: 'text-secondary',
      trend: 'stable'
    },
    {
      dimension: 'Mind',
      score: (data.headspace_level || 5) * 10,
      icon: Brain,
      color: 'text-primary',
      trend: 'stable'
    },
    {
      dimension: 'Spirit',
      score: calculateSpiritScore(data),
      icon: Sparkles,
      color: 'text-secondary',
      trend: 'stable'
    },
    {
      dimension: 'Social',
      score: calculateSocialScore(data),
      icon: Users,
      color: 'text-accent',
      trend: 'stable'
    },
    {
      dimension: 'Purpose',
      score: calculatePurposeScore(data),
      icon: Target,
      color: 'text-secondary',
      trend: 'stable'
    }
  ];
}

function calculateBodyScore(data: any): number {
  let score = 50; // Base score
  
  // Sleep quality contributes 30%
  if (data.sleep_quality) score += (data.sleep_quality * 3);
  
  // Exercise contributes 20%
  if (data.exercise_completed) score += 20;
  else if (data.exercise_duration > 0) score += 10;
  
  // Hydration contributes 10%
  if (data.water_intake >= data.water_target) score += 10;
  
  // Movement contributes 10%
  if (data.nature_time_minutes > 0) score += 10;
  
  return Math.min(Math.round(score), 100);
}

function calculateEmotionScore(data: any): number {
  // Positive emotions = higher score
  const positiveStates = ['Joy & Contentment', 'Peace & Calm'];
  const negativeStates = ['Anxiety & Fear', 'Sadness & Grief', 'Anger'];
  
  if (positiveStates.includes(data.emotion_field)) return 80;
  if (negativeStates.includes(data.emotion_field)) return 40;
  return 60;
}

function calculateSpiritScore(data: any): number {
  const connectionStates = ['Deeply Connected', 'Open & Receptive'];
  if (connectionStates.includes(data.spirit_field)) return 80;
  if (data.spirit_field === 'Disconnected') return 30;
  return 60;
}

function calculateSocialScore(data: any): number {
  const connectedStates = ['Connected & Supported', 'Open & Present'];
  if (connectedStates.includes(data.relational_field)) return 80;
  if (data.relational_field === 'Withdrawn') return 30;
  return 60;
}

function calculatePurposeScore(data: any): number {
  let score = 50;
  
  // Life expansion activity completed
  if (data.life_expansion_completed) score += 30;
  
  // Has manifestation focus
  if (data.manifestation_focus) score += 20;
  
  return Math.min(Math.round(score), 100);
}
